import React, { useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon, ChevronsLeftIcon, ChevronsRightIcon, DownloadIcon, ArrowUpIcon, ArrowDownIcon } from 'lucide-react';
import Button from './Button';
interface TableProps {
  columns: {
    key: string;
    header: string;
    sortable?: boolean;
  }[];
  data: any[];
  onExport: (type: 'xlsx' | 'csv' | 'pdf') => void;
}
const Table: React.FC<TableProps> = ({
  columns,
  data,
  onExport
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(10);
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  // Sort data if necessary
  const sortedData = [...data].sort((a, b) => {
    if (!sortColumn) return 0;
    const aValue = a[sortColumn];
    const bValue = b[sortColumn];
    if (aValue === bValue) return 0;
    if (sortDirection === 'asc') {
      return aValue < bValue ? -1 : 1;
    } else {
      return aValue > bValue ? -1 : 1;
    }
  });
  const currentRecords = sortedData.slice(indexOfFirstRecord, indexOfLastRecord);
  const totalPages = Math.ceil(data.length / recordsPerPage);
  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };
  const handleRecordsPerPageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setRecordsPerPage(parseInt(e.target.value));
    setCurrentPage(1);
  };
  const handleSort = (columnKey: string) => {
    if (sortColumn === columnKey) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(columnKey);
      setSortDirection('asc');
    }
  };
  return <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden" data-id="element-198">
      <div className="px-5 py-4 border-b border-gray-200 flex justify-between items-center bg-gray-50" data-id="element-199">
        <div className="text-sm text-gray-600" data-id="element-200">
          Showing {indexOfFirstRecord + 1} to{' '}
          {Math.min(indexOfLastRecord, data.length)} of {data.length} entries
        </div>
        <div className="flex space-x-2" data-id="element-201">
          <Button variant="outline" size="sm" icon={<DownloadIcon className="h-4 w-4" data-id="element-203" />} onClick={() => onExport('xlsx')} data-id="element-202">
            XLSX
          </Button>
          <Button variant="outline" size="sm" icon={<DownloadIcon className="h-4 w-4" data-id="element-205" />} onClick={() => onExport('csv')} data-id="element-204">
            CSV
          </Button>
          <Button variant="outline" size="sm" icon={<DownloadIcon className="h-4 w-4" data-id="element-207" />} onClick={() => onExport('pdf')} data-id="element-206">
            PDF
          </Button>
        </div>
      </div>
      <div className="overflow-x-auto" data-id="element-208">
        <table className="min-w-full divide-y divide-gray-200" data-id="element-209">
          <thead className="bg-gray-50" data-id="element-210">
            <tr data-id="element-211">
              {columns.map(column => <th key={column.key} scope="col" className={`px-6 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider ${column.sortable ? 'cursor-pointer hover:bg-gray-100' : ''}`} onClick={() => column.sortable && handleSort(column.key)} data-id="element-212">
                  <div className="flex items-center space-x-1" data-id="element-213">
                    <span data-id="element-214">{column.header}</span>
                    {column.sortable && sortColumn === column.key && (sortDirection === 'asc' ? <ArrowUpIcon className="h-4 w-4 text-blue-600" data-id="element-215" /> : <ArrowDownIcon className="h-4 w-4 text-blue-600" data-id="element-216" />)}
                  </div>
                </th>)}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200" data-id="element-217">
            {currentRecords.length > 0 ? currentRecords.map((record, index) => <tr key={index} className="hover:bg-gray-50 transition-colors duration-150" data-id="element-218">
                  {columns.map(column => <td key={column.key} className="px-6 py-4 whitespace-nowrap text-sm text-gray-700" data-id="element-219">
                      {record[column.key]}
                    </td>)}
                </tr>) : <tr data-id="element-220">
                <td colSpan={columns.length} className="px-6 py-8 whitespace-nowrap text-sm text-gray-500 text-center bg-gray-50" data-id="element-221">
                  <div className="flex flex-col items-center justify-center" data-id="element-222">
                    <svg className="h-12 w-12 text-gray-400 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-223">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M12 20h.01M12 14h.01M12 8h.01M12 4h.01" data-id="element-224" />
                    </svg>
                    <span className="text-gray-500 font-medium" data-id="element-225">
                      No records found
                    </span>
                    <p className="text-gray-400 text-xs mt-1" data-id="element-226">
                      Try adjusting your search or filter criteria
                    </p>
                  </div>
                </td>
              </tr>}
          </tbody>
        </table>
      </div>
      <div className="px-5 py-4 border-t border-gray-200 flex items-center justify-between bg-gray-50" data-id="element-227">
        <div className="flex items-center" data-id="element-228">
          <span className="text-sm text-gray-700 mr-2" data-id="element-229">Show</span>
          <select value={recordsPerPage} onChange={handleRecordsPerPageChange} className="border-gray-300 rounded-md text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white shadow-sm" data-id="element-230">
            <option value={10} data-id="element-231">10</option>
            <option value={25} data-id="element-232">25</option>
            <option value={50} data-id="element-233">50</option>
            <option value={100} data-id="element-234">100</option>
          </select>
          <span className="text-sm text-gray-700 ml-2" data-id="element-235">entries</span>
        </div>
        <div className="flex items-center space-x-1" data-id="element-236">
          <button onClick={() => handlePageChange(1)} disabled={currentPage === 1} className="px-2 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-237">
            <ChevronsLeftIcon className="h-4 w-4" data-id="element-238" />
          </button>
          <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} className="px-2 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-239">
            <ChevronLeftIcon className="h-4 w-4" data-id="element-240" />
          </button>
          <span className="px-3 py-1 text-sm text-gray-700" data-id="element-241">
            Page {currentPage} of {totalPages}
          </span>
          <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} className="px-2 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-242">
            <ChevronRightIcon className="h-4 w-4" data-id="element-243" />
          </button>
          <button onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages} className="px-2 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-244">
            <ChevronsRightIcon className="h-4 w-4" data-id="element-245" />
          </button>
        </div>
      </div>
    </div>;
};
export default Table;